import type { QueryClient } from '@tanstack/react-query';
import { redirect } from '@tanstack/react-router';
import { ApiError } from '@wundo/client/api.ts';
import { type RoleplaySessionResponse, fetchRoleplaySession } from './roleplaySession.ts';

/**
 * The guard on one practice conversation, which is the only screen in the app
 * two different kinds of visitor reach.
 *
 * It asks for the conversation rather than for the person. Somebody who opened
 * a link from a chat message holds a session good for this one thing and
 * nothing else — asking who they are would be refused, and would bounce a
 * perfectly valid visitor to a login screen they have no password for. Asking
 * for the conversation is the same question for both kinds of visitor, and the
 * only one that has to be true for the screen to work.
 *
 * The answer is left in the cache the screen reads from, so the conversation is
 * fetched once rather than twice.
 *
 * Only "no session at all" goes to the login screen, carrying this address so
 * sign-in returns here. Every other refusal is left to the screen, which says
 * which one it was — a conversation that is not theirs has nothing to gain
 * from signing in again.
 */
export async function requireRoleplaySession(
  queryClient: QueryClient,
  sessionId: string,
  href: string,
): Promise<void> {
  try {
    await queryClient.ensureQueryData<RoleplaySessionResponse>({
      queryKey: ['roleplaySession', sessionId],
      queryFn: () => fetchRoleplaySession(sessionId),
    });
  } catch (caught) {
    if (caught instanceof ApiError && caught.status === 401) {
      /*
       * Absolute, because the backend honours a return address by its origin
       * and reads a relative one as naming none.
       */
      throw redirect({
        to: '/login',
        search: {
          redirect: `${window.location.origin}${href}`,
          error: undefined,
        },
        replace: true,
      });
    }
    /* The screen reads the same failure out of the query and names it. */
  }
}

/**
 * Why a practice conversation could not be opened, for the screen that has to
 * say so.
 *
 * Three answers, because they need three different things from the reader.
 * `missing` is a conversation that is not there or not theirs. `impersonated`
 * is a staff member signed in as someone else, whose way out is their own
 * account. `unreachable` is our problem, and worth waiting out.
 */
export type RoleplaySessionFailure = 'missing' | 'impersonated' | 'unreachable';

export function roleplaySessionFailure(caught: unknown): RoleplaySessionFailure {
  if (!(caught instanceof ApiError)) {
    return 'unreachable';
  }

  /*
   * Named before the status codes: it is also a 403, but "sign in as
   * yourself" is a different instruction than "this is not yours".
   */
  if (caught.code === 'impersonation_blocked') {
    return 'impersonated';
  }

  /*
   * A 401 that reaches the screen is a session that died after the guard let
   * the page through, so it reads the same as a conversation that is not
   * theirs: there is nothing here for them either way.
   */
  if (caught.status === 401 || caught.status === 403 || caught.status === 404) {
    return 'missing';
  }

  return 'unreachable';
}
